import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

/** T101VendorPerformance (GET /v1/vendor/performance, §21). */
interface Performance {
  readonly computedForDate: string;
  readonly lookbackDays: number;
  readonly totalAssignments: number;
  readonly acceptedCount: number;
  readonly rejectedCount: number;
  readonly completedCount: number;
  readonly delayedCount: number;
  readonly acceptancePct: string | null;
  readonly completionPct: string | null;
  readonly rejectionPct: string | null;
  readonly delayPct: string | null;
  readonly avgResponseMinutes: string | null;
  readonly avgCompletionHours: string | null;
  readonly escalationCount: number;
  readonly qualityScore: string | null;
}

/** VendorWorkloadView (GET /v1/vendor/workload, §17). */
interface Workload {
  readonly totalJobs: number;
  readonly pendingAcceptance: number;
  readonly painterPending: number;
  readonly executionInProgress: number;
  readonly overdueJobs: number;
  readonly capacity: number;
  readonly utilisationPct: number;
}

const METRICS: ReadonlyArray<{ readonly key: keyof Performance; readonly labelKey: string }> = [
  { key: 'totalAssignments', labelKey: 'vops.performance.metric.totalAssignments' },
  { key: 'acceptedCount', labelKey: 'vops.performance.metric.accepted' },
  { key: 'rejectedCount', labelKey: 'vops.performance.metric.rejected' },
  { key: 'completedCount', labelKey: 'vops.performance.metric.completed' },
  { key: 'delayedCount', labelKey: 'vops.performance.metric.delayed' },
  { key: 'acceptancePct', labelKey: 'vops.performance.metric.acceptancePct' },
  { key: 'completionPct', labelKey: 'vops.performance.metric.completionPct' },
  { key: 'rejectionPct', labelKey: 'vops.performance.metric.rejectionPct' },
  { key: 'delayPct', labelKey: 'vops.performance.metric.delayPct' },
  { key: 'avgResponseMinutes', labelKey: 'vops.performance.metric.avgResponse' },
  { key: 'avgCompletionHours', labelKey: 'vops.performance.metric.avgCompletion' },
  { key: 'escalationCount', labelKey: 'vops.performance.metric.escalations' },
  { key: 'qualityScore', labelKey: 'vops.performance.metric.qualityScore' },
];

export function VendorPerformancePage(): ReactNode {
  const { number } = useBusinessNumber();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [perf, setPerf] = useState<Performance | null>(null);
  const [workload, setWorkload] = useState<Workload | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [p, w] = await Promise.all([
          apiRequest<Performance | null>('/v1/vendor/performance', { token }),
          apiRequest<Workload>('/v1/vendor/workload', { token }),
        ]);
        if (alive) {
          setPerf(p);
          setWorkload(w);
          setLoaded(true);
        }
      } catch (e) {
        if (alive) setError((e as Error).message);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  if (error) return <main aria-label="vendor-performance"><p role="alert">{error}</p></main>;
  if (!loaded) return <main aria-label="vendor-performance"><p>{t('common.loading')}</p></main>;

  return (
    <main aria-label="vendor-performance">
      <h1>{t('vops.performance.title')}</h1>

      <section aria-label="workload">
        <h2>{t('vops.common.workload')}</h2>
        {workload ? (
          <p>
            {t('vops.performance.wlJobs')} <strong aria-label="wl-total">{workload.totalJobs}</strong> · {t('vops.performance.wlOverdue')}{' '}
            <strong aria-label="wl-overdue">{workload.overdueJobs}</strong> · {t('vops.performance.wlUtilisation')}{' '}
            <strong aria-label="wl-utilisation">{number(workload.utilisationPct)}%</strong> {t('vops.performance.wlOf')}{' '}
            <strong aria-label="wl-capacity">{workload.capacity}</strong>
          </p>
        ) : null}
      </section>

      <section aria-label="performance">
        <h2>{t('vops.performance.perfHeading', { detail: perf ? t('vops.performance.lookback', { days: perf.lookbackDays }) : t('vops.performance.noData') })}</h2>
        {perf === null ? (
          <p aria-label="performance-empty">{t('vops.performance.empty')}</p>
        ) : (
          <dl aria-label="performance-metrics">
            {METRICS.map((m) => (
              <div key={m.key}>
                <dt>{t(m.labelKey)}</dt>
                <dd aria-label={`metric-${m.key}`}>{perf[m.key] === null ? '—' : String(perf[m.key])}</dd>
              </div>
            ))}
          </dl>
        )}
      </section>
    </main>
  );
}
